import { connect } from 'react-redux';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";

import { saveOnboardingStep, getOnboardingStepData, isStepCompleted } from '../../utils/onboardingUtils';
import { hasCompletedOnboarding, getOnboardingRoute } from '../../utils/AuthorizationCheck';

const PlatformSelection = ({auth}) => {        
    const history = useHistory();
    const [selectedPlatform, setSelectedPlatform] = useState('sleeper'); // Visual default selection
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {                
        // Check if user is authenticated and has completed onboarding
        if (auth?.user?.id && hasCompletedOnboarding(auth)) {
            history.push('/dashboard');
            return;
        }
        
        // Check if user should be on a different onboarding step
        if (auth?.user?.id && !hasCompletedOnboarding(auth)) {
            const correctRoute = getOnboardingRoute(auth);
            if (correctRoute !== '/onboarding/platform') {
                history.push(correctRoute);
                return;
            }
        }
        
        // Check if user is authenticated and has completed this step
        if (auth?.user?.id && isStepCompleted('platform')) {
            //history.push('/onboarding/username');
            return;
        }

        // Load existing data from session storage if available
        const existingData = getOnboardingStepData('platform');
        if (existingData?.platform) {
            setSelectedPlatform(existingData.platform);
        }

        // Only clear username data if there's an error state from previous attempts
        const usernameData = getOnboardingStepData('username');
        if (usernameData && usernameData.error) {
            // Clear error state but preserve the username
            saveOnboardingStep('username', {
                username: usernameData.username,
                error: null
            });
        }

        // Don't save step completion until user actually completes the step
        // This ensures progress bar shows 0% initially
    }, [auth, history]);

    const handlePlatformSelect = (platform) => {
        // If clicking the already selected platform, deselect it
        if (selectedPlatform === platform) {
            setSelectedPlatform('');
        } else {
            setSelectedPlatform(platform);
        }
    };

    const handleContinue = async () => {
        if (!selectedPlatform) return;
        
        setIsSubmitting(true);
        
        try {
            // Save platform selection
            saveOnboardingStep('platform', { 
                platform: selectedPlatform
            });
            
            // Move to next step
            history.push('/onboarding/username');
        } catch (error) {
            console.error('Error saving platform selection:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getProgressPercentage = () => {
        // Platform is step 1 of 8, so 0% (not started yet)
        return 0;
    };

    return (
        <>
            <div className="pt-5 onboarding-section">    
                <Row className="onboarding-header">
                    <Col xs={1} md={2}></Col>
                    <Col xs={10} md={8}>       
                            
                        <Row className="text-center onboarding-title-row">
                            <Col className="onboarding-title">Choose Your Platform</Col>
                        </Row>
                        
                        {/* Progress Bar */}
                        <Row className="mt-4">
                            <Col>
                                <div className="text-center mb-1">
                                    <span className="onboarding-progress-percentage">
                                        {Math.round(getProgressPercentage())}% Complete
                                    </span>
                                </div>
                                <div className="onboarding-progress">
                                    <div 
                                        className="onboarding-progress-bar"
                                        style={{ width: `${getProgressPercentage()}%` }}
                                    ></div>
                                </div>
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col>
                                <div className="platform-options">
                                    {/* Sleeper Platform */}
                                    <div
                                        className={`platform-card ${selectedPlatform === 'sleeper' ? 'selected' : ''}`}
                                        onClick={() => handlePlatformSelect('sleeper')}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handlePlatformSelect('sleeper');
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        aria-label="Select Sleeper platform"
                                        aria-pressed={selectedPlatform === 'sleeper'}
                                    >
                                        <h3>Sleeper</h3>
                                    </div>
                                    <div
                                        className="platform-card coming-soon"
                                        role="status"
                                        aria-label="ESPN platform coming soon"
                                    >
                                        <h3>ESPN</h3>
                                        <p>Coming soon</p>
                                    </div>
                                    <div
                                        className="platform-card coming-soon"
                                        role="status"
                                        aria-label="Yahoo! platform coming soon"
                                    >
                                        <h3>Yahoo!</h3>
                                        <p>Coming soon</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        <Row className='align-items-center mt-4 justify-content-center'>
                            <Button 
                                onClick={handleContinue}
                                disabled={!selectedPlatform || isSubmitting}
                                className={`onboarding-next-btn ${!selectedPlatform ? 'btn-secondary' : 'btn-primary-gradient'}`}
                            >
                                {isSubmitting ? 'Saving...' : 'Next →'}
                            </Button>
                        </Row>                                                 
                    </Col>                            
                </Row>
            </div>
        </>
    );
};

function mapStateToProps({auth}) {
    return { auth };
}

export default connect(mapStateToProps)(PlatformSelection);
